import "maplibre-gl/dist/maplibre-gl.css";

import { MapController, MapView } from "@deck.gl/core";
import DeckGL from "@deck.gl/react";
import { BoxSelect } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import * as maplibre from "react-map-gl/maplibre";

import { useOverlayMap } from "~/lib/map/hooks/useOverlayMap";
import type { ResolutionType, ViewportType } from "~/lib/map/types";

import { mapStyle } from "./constants";

interface OverlayMapProps {
  height: number;
  resolution: ResolutionType;
  areaH3Indexes: string[];
  overlayImageUrls: string[];
  overlayOpacity: number;
  dragOverlayImageMode: boolean;
  onHexagonClick: (h3Index: string) => void;
  initialViewState?: Partial<ViewportType>;
}

/** GeoTIFF-overlay area map: drag/georeference overlay images, paint H3 cells. */
export function OverlayMap({ height, ...canvasProps }: OverlayMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-[var(--radius-md)] border border-border-default"
      style={{ height }}
    >
      {/* deck.gl needs pixel dimensions up front, so hold the canvas back until the container is measured. */}
      {width > 0 && <OverlayMapCanvas width={width} height={height} {...canvasProps} />}
    </div>
  );
}

function OverlayMapCanvas({
  width,
  height,
  resolution,
  areaH3Indexes,
  overlayImageUrls,
  overlayOpacity,
  dragOverlayImageMode,
  onHexagonClick,
  initialViewState,
}: OverlayMapProps & { width: number }) {
  const {
    layers,
    viewState,
    onViewStateChange,
    handleMouseDown,
    handleHover,
    handleDrag,
    handleMouseUp,
    isOverlayImageDragging,
    isMultiSelectMode,
  } = useOverlayMap({
    width,
    height,
    resolution,
    areaH3Indexes,
    overlayImageUrls,
    overlayOpacity,
    dragOverlayImageMode,
    onHexagonClick,
    initialViewState,
  });

  return (
    <>
      <DeckGL
        id="overlay-map"
        initialViewState={viewState}
        layers={layers}
        views={new MapView({ repeat: true })}
        onViewStateChange={onViewStateChange}
        onClick={handleMouseDown}
        onHover={handleHover}
        onDragStart={handleMouseDown}
        onDrag={handleDrag}
        onDragEnd={handleMouseUp}
        getCursor={({ isDragging }) => {
          if (isOverlayImageDragging) return "crosshair";
          return isDragging ? "grabbing" : "grab";
        }}
        controller={{
          type: MapController,
          keyboard: true,
          dragRotate: false,
          doubleClickZoom: false,
          dragPan: !isOverlayImageDragging,
        }}
        width="100%"
        height={height}
      >
        <maplibre.Map key="overlay-map" mapStyle={mapStyle} dragRotate={false} interactive={!isOverlayImageDragging} />
      </DeckGL>
      {isMultiSelectMode && (
        <div className="absolute left-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-bg-surface/80 text-primary-500">
          <BoxSelect className="h-4 w-4" />
        </div>
      )}
    </>
  );
}
